import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import User from "../../models/User";
import {CarryOutOutlined} from '@ant-design/icons';
import {useMutation} from "@tanstack/react-query";
import {Alert, Input, Modal, Tree} from 'antd';
import {Link, useLocation} from "react-router-dom";
import {useAuth} from "../../providers/AuthProvider";
import Form, {FormInputComponent} from '../../components/form/Form';
import TalentCandidate from '../../models/TalentCandidate';
import LoadingSpinner from '../../components/LoadingSpinner';
import SidebarComponent from "../../components/SidebarComponent";

import {createBreakpoint} from "react-use";

const useBreakpoint = createBreakpoint({ tablet: 768, mobile: 0 });

const ApplicationSideNavBar = ({ steps, currentStep, disabled, onStepNavigate, currentStepIndex, ...props }) => {
    const { auth } = useAuth();

    const breakpoint = useBreakpoint();
    const location = useLocation()
    const talentUser = props.user || auth;

    const isAdminEditing = auth?.canManageClientResourceTalent() && !!props.user;

    const talentSteps = useMemo(() => {

        if (!talentUser.isApplicationApproved()) {
            return steps
        }

        return TB.groupBy(steps, step => step.StepSection)
    }, [talentUser?.isApplicationApproved(), steps]);

    const sideBarTreeMenu = useMemo(() => {
        if (TB.isArray(talentSteps)) {
            return []
        }
        let menu = []
        TB.map(talentSteps, (sectionSteps, sectionKey) => {

            menu.push({
                title: sectionKey,
                key: `${TB.slugify(sectionKey)}`,
                icon: <CarryOutOutlined />,
                children: TB.map(sectionSteps, (step, index) => {
                    if (!step.StepTitle) return null;

                    let needsCompletion = step.NeedsCompletion(talentUser);

                    let isActive = currentStep.StepTitle === step.StepTitle;
                    let isCompleted =
                        (!needsCompletion && index <= currentStepIndex) ||
                        step.hasCompleted(talentUser || {});


                    return {
                        title: (
                            <div
                                className={TB.mapClass('flex flex-row items-center sub-step-title step-title py-0 m-0 h-7', {
                                    isApproved: talentUser.isApplicationApproved(),
                                    isCompleted,
                                    isActive,
                                    'no-click': disabled
                                })}>
                                <i className={TB.mapClass("", {
                                    "fas fa-check-circle": needsCompletion && isCompleted,
                                    "fas fa-times-circle": needsCompletion && !isCompleted,
                                    "fas fa-arrow-alt-circle-right": !needsCompletion,
                                })} />

                                {step.StepTitle}
                            </div>
                        ),
                        key: `${TB.slugify(step.StepTitle)}`,
                    }
                })
            })
        })
        return menu
    }, [location?.pathname, talentSteps, talentUser, disabled]);
    if (!talentUser) return <div className="talent-app-nav lg:w-3/12 p-0" />


    return (

        <SidebarComponent canClose={breakpoint === 'mobile'} maxWidth="18rem" defaultClosed={false}
            openIcon={<i className="fas fa-caret-right" />}>
            <div className={TB.mapClass("talent-app-nav p-0 md:max-w-72", {})}>

                <TalentProfileStatus talentUser={talentUser} />

                <h3 className="font-bold px-2">
                    {!talentUser.isApplicationApproved() ?
                        'Complete Your Application' :
                        'Edit Your Profile'
                    }
                </h3>


                {TB.isArray(talentSteps) ? (
                    talentSteps.map((step, index) => {
                        if (!step.StepTitle) return null;

                        const isActive = currentStep.StepTitle === step.StepTitle;
                        const isPrevious = index <= currentStepIndex;
                        const isNext = index > currentStepIndex;

                        const importActive = User.getResumeImportsForUser(talentUser?.id);
                        let needsCompletion = step.NeedsCompletion(talentUser);

                        const isCompleted = (!needsCompletion && isPrevious) ||
                            step.hasCompleted(talentUser || {});
                        const canGoToNext = isPrevious || (isCompleted && index === (currentStepIndex + 1));
                        return (
                            <button
                                type="button"
                                key={`step-nav-${index}`}
                                className={TB.mapClass('step-title pr-2', {
                                    isApproved: talentUser.isApplicationApproved(),
                                    isPrevious,
                                    isNext,
                                    canGoToNext,
                                    isCompleted,
                                    isActive,
                                    'no-click': disabled
                                })}
                                onClick={() => onStepNavigate(step)}
                            >
                                <i className={TB.mapClass("fas", {
                                    "fa-check-circle": needsCompletion && isCompleted,
                                    "fa-times-circle": needsCompletion && !isCompleted,
                                    "fa-arrow-alt-circle-right": !needsCompletion,
                                })} />

                                {step.StepTitle}
                            </button>
                        )

                    })
                ) : (
                    <React.Fragment>
                        <div className="pl-5">
                            <Tree
                                showLine={true}
                                showIcon={false}
                                defaultExpandedKeys={TB.map(sideBarTreeMenu, key => TB.slugify(key.key))}
                                onSelect={(selectedKeys, info) => {
                                    let key = TB.first(selectedKeys);
                                    if (!key) return;
                                    // console.log('onSelect', key, info)
                                    let step = TB.find(TB.flatMap(TB.values(talentSteps)), step => TB.slugify(step.StepTitle) === key);
                                    if (step) {
                                        // console.log('onstepnav', step)
                                        return onStepNavigate(step)
                                    }
                                }}
                                treeData={TB.map(talentSteps, (sectionSteps, sectionKey) => {
                                    return {
                                        title: sectionKey,
                                        key: `${TB.slugify(sectionKey)}`,
                                        icon: <CarryOutOutlined />,
                                        children: TB.map(sectionSteps, (step, index) => {
                                            if (!step.StepTitle) return null;

                                            let needsCompletion = step.NeedsCompletion(talentUser);

                                            let isActive = currentStep.StepTitle === step.StepTitle;
                                            let isCompleted =
                                                (!needsCompletion && index <= currentStepIndex) ||
                                                step.hasCompleted(talentUser || {});


                                            if (isActive) {
                                                // console.log(step.StepTitle, isCompleted, talentUser.validateWorkPreferences());
                                            }
                                            return {
                                                title: (
                                                    <div
                                                        className={TB.mapClass('profile-tree-link flex flex-row items-center sub-step-title step-title py-0 m-0 h-7', {
                                                            isApproved: talentUser.isApplicationApproved(),
                                                            isCompleted,
                                                            isActive,
                                                            'no-click': disabled
                                                        })}>
                                                        <i className={TB.mapClass("", {
                                                            "fas fa-check-circle": needsCompletion && isCompleted,
                                                            "fas fa-times-circle": needsCompletion && !isCompleted,
                                                            "fas fa-arrow-alt-circle-right": !needsCompletion,
                                                        })} />

                                                        {step.StepTitle}
                                                    </div>
                                                ),
                                                key: `${TB.slugify(step.StepTitle)}`,
                                            }
                                        })
                                    }
                                })}
                            />
                        </div>
                    </React.Fragment>
                )}

            </div>
        </SidebarComponent>
    )
};

ApplicationSideNavBar.propTypes = {
    steps: PropTypes.any,
    currentStep: PropTypes.any,
    disabled: PropTypes.any,
    onStepNavigate: PropTypes.any,
};

export default ApplicationSideNavBar;

const TalentProfileStatus = ({ talentUser }) => {
    const { talentUpdate, submitAppealForRejectedProfile } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    // const isAdminEditing = talentUser?.canManageClientResourceTalent() && !!props.user;

    // console.log(talentUser, 'TALENT USER')

    if (!talentUser.isApplicationApproved()) return null;

    // if (!talentUser.isApplicationApproved() || !talentUser.isTalentSupplierResource() && !isAdminEditing)
    let status;
    if (talentUser.isPendingProfileApproved()) { // pending
        // if (true) {
        status = {
            className: 'bg-yellow-100 text-yellow-800',
            title: 'Your profile is pending approval',
            icon: 'fas fa-history',
            subTitle: 'Pending Approval',
        }
    } else if (talentUser.isProfileApproved()) {
        status = {
            className: 'bg-green-100 text-green-800',
            title: 'Your profile is approved',
            icon: 'fas fa-check-circle text-wripplePrimaryPaleGreen',
        }
    } else if (talentUser.latest_appeal) {

        if (talentUser.latest_appeal.is_rejected) {
            status = {
                className: 'bg-red-100 text-red-800',
                title: 'Your appeal is not approved',
                icon: 'fas fa-times-circle text-wripplePrimaryPaleRed',
                subTitle: 'Rejected',
            }
        } else {
            status = {
                className: 'bg-red-100 text-red-800',
                title: 'Your profile is pending appeal',
                icon: 'fas fa-history',
                subTitle: 'Appeal Sent',
            }
        }
    } else if (talentUser.isProfileRejected()) {
        status = {
            className: 'bg-red-100 text-red-800',
            title: 'Your profile is not approved',
            icon: 'fas fa-times-circle text-wripplePrimaryPaleRed',
            subTitle: 'Rejected',
        }
    } else {
        status = {
            className: 'bg-yellow-100 text-yellow-800',
            title: 'Your profile is not approved',
            icon: 'fas fa-times-circle text-wripplePrimaryPaleRed',
            subTitle: !talentUser.hasCompletedProfile() ? 'Missing Required Steps' : 'Ready For Approval',
        }
    }


    return (
        <div className="bg-red-100 p3 mb-5">

            {isLoading && (
                <div
                    className="fixed backdrop-blur-sm transition bg-white/30 inset-0 z-50 flex items-center justify-center">
                    <LoadingSpinner size={'lg'} />
                </div>
            )}


            <div style={{ background: '#f4f4f6' }} className="px-2 py-4">
                <div>

                    <div className="flex gap-2 items-center justify-center">
                        <i className={status.icon} />
                        <p>{status.title}</p>
                    </div>

                    {!talentUser.isProfileApproved() && (
                        <div className="text-center pt-2">
                            <p className='text-xs'>Status: <strong>{status.subTitle}</strong></p>
                        </div>
                    )}

                    <div className="flex justify-between gap-3 pt-3.5">
                        <Link
                            to={`/talent-profiles/${talentUser.id}`}
                            className="btn btn-dark flex-1 max-w-[unset] md:max-w-2/3 text-xxs mx-auto py-1 px-2 text-center"
                        >
                            Preview <br className={talentUser.isProfileApproved() ? 'hidden' : undefined} /> Client View
                        </Link>

                        {/* {talentUser.isProfileRejected() && talentUser.can_submit_appeal && ( */}
                        {talentUser.isProfileRejected() && (
                            <ProfileAppealModal isAppeal={!!talentUser.latest_appeal} talentUser={talentUser} />
                        )}

                        {!talentUser.isProfileApproved() && !talentUser.isProfileRejected() && (
                            <button
                                type="button"
                                className="btn btn-primary flex-1 max-w-[unset] md:max-w-2/3 text-xxs mx-auto py-1 px-2"
                                disabled={talentUser.isPendingProfileApproved() || !talentUser.hasCompletedProfile()}
                                onClick={async () => {
                                    const confirm = await TB.alert('success', 'Submit Profile for Approval',
                                        'Are you sure you want to submit your profile for review? Our talent team will review your profile and provide feedback if necessary. Once approved, you will be able to join access the job board and apply for project opportunities.',
                                        [{ title: 'Yes' }, { title: 'Cancel' }]
                                    );

                                    if (confirm) {
                                        setIsLoading(true)
                                        const res = await talentUpdate({ talent_profile_review_requested_at: new Date() })
                                        if (!!res) {
                                            TB.toast("Profile submitted")
                                        }
                                        setIsLoading(false)
                                    }
                                }}
                            >
                                Submit For <br /> Approval
                            </button>
                        )}

                    </div>
                </div>
            </div>
        </div>
    )
}


const ProfileAppealModal = ({ isAppeal, talentUser }) => {
    const [isAppealModalOpen, setIsAppealModalOpen] = useState(false);
    const [showAppealForm, setShowAppealForm] = useState(false);

    const { update: updateAuth } = useAuth();

    const handleClose = () => {
        setIsAppealModalOpen(false);
        setShowAppealForm(false);
    }

    const addTalentProfileAppealMutation = useMutation(['applicationProfileAppeal'],
        params => TalentCandidate.addTalentProfileAppeal(params),
        {
            onSuccess: async data => {
                handleClose();

                const updatedUser = User.make({ ...talentUser, latest_appeal: data.appeal });
                const didUpdate = await updateAuth(updatedUser);

                if (didUpdate) {
                    TB.alert('success', 'Profile Appeal Submitted', 'A talent manager will review your appeal and will reach out via email.')
                }
            },
            onError: err => {
                console.log(err, 'Error addTalentProfileAppealMutation');
            }
        }
    )

    return (
        <>
            <button
                type="button"
                className="btn btn-primary flex-1 max-w-[unset] md:max-w-2/3 text-xxs mx-auto py-1 px-2"
                onClick={() => setIsAppealModalOpen(true)}
            >
                View <br /> Feedback
            </button>

            <Modal
                title={`${isAppeal ? 'Appeal' : 'Profile'} Not Approved`}
                closable={true}
                maskClosable={true}
                style={{ top: 150 }}
                destroyOnClose={true}
                width={500}
                footer={null}
                open={isAppealModalOpen}
                onCancel={handleClose}
                onClose={handleClose}
            >

                {showAppealForm ? (
                    <Form
                        showRequired
                        background={'transparent'}
                        title={null}
                        subtitle={null}
                        submitButton={false}
                        className="[&>.form-container]:p-0"
                        style={{ padding: 20 }}
                        onSubmit={async fields => addTalentProfileAppealMutation.mutateAsync(fields)}
                        isDisabled={addTalentProfileAppealMutation.isLoading}
                        fields={[]}
                    >
                        <div>
                            <label htmlFor="appeal-reason" className="block pb-1">
                                Write a note to explain your reason for your profile appeal. After your appeal is
                                submitted, a Wripple Talent Manager will review and reach out via email if there are any
                                questions.
                            </label>

                            <FormInputComponent
                                fieldName="notes"
                                disabled={addTalentProfileAppealMutation.isLoading}
                                required
                                label="Notes"
                                type="antd-input"
                            >
                                <Input.TextArea
                                    showCount
                                    bordered
                                    id="appeal-reason"
                                    autoSize={{ minRows: 4 }}
                                    maxLength={500}
                                />
                            </FormInputComponent>
                        </div>
                        <div className="flex flex-row justify-between items-center gap-3">
                            <div className="mr-auto">
                                <button
                                    type="button"
                                    className="btn btn-link"
                                    onClick={() => setShowAppealForm(false)}
                                >
                                    Back
                                </button>

                                {addTalentProfileAppealMutation.failureReason && (
                                    <p className="text-danger mr-auto">{addTalentProfileAppealMutation.failureReason?.message}</p>
                                )}
                            </div>
                            <button
                                type="button"
                                className="btn btn-outline-dark"
                                disabled={addTalentProfileAppealMutation.isLoading}
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={addTalentProfileAppealMutation.isLoading}
                            >
                                Submit
                            </button>
                        </div>
                    </Form>
                ) : (
                    <>
                        <div>
                            <p>
                                Your {isAppeal ? 'appeal' : 'profile'} is not approved at this time.
                                Further feedback from the Wripple Talent Team has been provided below.
                                {!isAppeal ? ' If you believe this decision is incorrect, you may request an appeal at any time using the button below.' : ''}
                            </p>

                            <Alert
                                className="mt-4"
                                description={(
                                    <div>
                                        <p className="text-xs text-gray-500">
                                            Wripple Feedback:
                                        </p>

                                        <p className="pt-2">
                                            {talentUser.latest_appeal?.rejection_notes || talentUser.talent_profile_rejection_notes}
                                        </p>
                                    </div>
                                )}
                                type="warning"
                            />

                            {talentUser.latest_appeal && (
                                <div className="pt-4">
                                    <p>Your latest appeal pending review:</p>
                                    <Alert
                                        className="mt-2.5"
                                        description={(
                                            <div>
                                                <p className="text-xs text-gray-500">
                                                    Your Appeal:
                                                </p>

                                                <p className="pt-2">
                                                    {talentUser.latest_appeal.notes}
                                                </p>
                                            </div>
                                        )}
                                        type="info"
                                    />
                                </div>
                            )}
                        </div>
                        <div className="pt-5 flex flex-row justify-between gap-3">
                            {/* {(talentUser.latest_appeal.is_approved || talentUser.latest_appeal.is_rejected) && ( */}
                            <button
                                type="button"
                                className="btn btn-link"
                                onClick={() => {
                                    if (talentUser.latest_appeal?.is_approved === false && talentUser.latest_appeal?.is_rejected === false) {
                                        handleClose();
                                        return TB.alert('warning', "Can't resubmit appeal at this time", 'Your current appeal is pending review.')
                                    }
                                    // if (talentUser.can_submit_appeal) {
                                    setShowAppealForm(true)
                                    // } else {
                                    //     handleClose();
                                    //     TB.alert('warning', "Can't resubmit appeal at this time", 'Your last appeal was not approved. You can resubmit an appeal 1 year from the date it was not approved if you still disagree with this decision.')
                                    // }
                                }}
                            >
                                Submit Appeal
                            </button>
                            {/* )} */}

                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleClose}
                            >
                                Close
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </>
    )
}